import {
  EMPTY_RESULT,
  type ExpertReportResult,
  formatScoreWithUnit,
  getEffectiveEvaluationResult,
  getExpertReportResultText,
} from '@/entities/expert-report';

/** Результат строки заключения: чем оценены блок или итог и что из этого выставил пользователь */
export type EvaluationRowResult = {
  /** Балл, рассчитанный системой; null — блок оценён значением справочника */
  rating: number | null;
  maxRating: number | null;
  /** Балл, выставленный пользователем; null — балл не выставлялся */
  manualRating: number | null;
  /** Значение, полученное системой; null — результат блока оказался баллом */
  value: string | null;
  /** Набор значений, собранный правилом расчёта; null — результат не набор */
  values: string[] | null;
  /** Значение, выбранное пользователем; null — значение не выбиралось */
  manualValue: string | null;
  /** Числовая часть шкалы блока; не null только у блока, шкала которого несёт и баллы, и справочник */
  possiblePoints: number[] | null;
  /** Справочник, из которого выбирается значение блока; null — справочника у блока нет */
  possibleValues: string[] | null;
  /** Размерность балла; null — размерность не определена или результат не балл */
  unit: string | null;
};

/**
 * Текст результата в том виде, в каком он стоит в строке заключения: набор значений перечислением,
 * значение справочника, балл с максимумом и размерностью либо прочерк. Максимум стоит у балла,
 * которому он известен.
 *
 * Той же записью показываются версии результата блока в его истории: одна величина в строке и в
 * истории читается одинаково.
 */
export const getEvaluationResultText = (result: ExpertReportResult): string => {
  if (result.score != null && result.maxScore != null) {
    return formatScoreWithUnit(result.score, result.maxScore, result.unit);
  }
  return getExpertReportResultText(result) ?? EMPTY_RESULT;
};

/**
 * Текст результата строки заключения — тот же, что стоит в ней без правки.
 *
 * Какой из результатов записи действует, решает сама сущность ({@link getEffectiveEvaluationResult}):
 * второй разбор тех же полей разошёлся бы с показанным в строке — у блока с совмещённой шкалой
 * выставленный балл старше полученного значения. Вид задаёт {@link getEvaluationResultText}.
 *
 * Тем же текстом результат меряется на умещение ({@link useTotalRowFit}), поэтому вид его задаёт
 * общая запись, а не разметка каждого места показа.
 */
export const getEvaluationRowResultText = (result: EvaluationRowResult): string =>
  getEvaluationResultText(getEffectiveEvaluationResult(result));
